import { filterChips, isShownByQuickFilter } from './filter-bar/filterChips';
import { MainFilterForm, QuickFilterForm } from './ListTableTypes';

/**
 * Whether the current view is narrowed by something the user can undo — the signal that separates
 * "this tenant has no records" from "your filters matched nothing".
 *
 * A quick filter counts only when it DIFFERS from its `defaultSelected` baseline: `clearFilters`
 * restores the defaults rather than emptying the state, so on a surface whose quick filter ships
 * selected (Law firms) a virgin visit must read as tenant-empty, and offering "Clear filters" there
 * would be a button that changes nothing.
 *
 * Main filters have no default-selected concept — `clearFilters` drops even the ones a page seeded
 * through `initialSearchParams` — so any active main filter counts, via the hook's `mainFilterCount`.
 * A main filter on a quick filter's key sends its clause in `qf`, outside that count, and the quick
 * filter mirrors it. One the quick filter cannot show (an "is not", a value it does not list) leaves
 * the quick options on their baseline, so it counts here, as the filter row draws its own chip.
 */
export const hasUserFilters = ({
    mainFilterCount,
    mainFilters,
    quickFilters,
    searchText,
}: {
    mainFilterCount: number;
    mainFilters: MainFilterForm;
    quickFilters: QuickFilterForm;
    searchText: string;
}): boolean => {
    if (mainFilterCount > 0 || searchText.trim().length > 0) return true;
    const chips = filterChips({ mainFilterState: mainFilters, quickFilterState: quickFilters }, String);
    const isHeldOffQuickFilter = chips.some(
        ({ field }) => field.kind === 'main' && !!quickFilters[field.key] && !isShownByQuickFilter(field, quickFilters)
    );
    if (isHeldOffQuickFilter) return true;

    return Object.values(quickFilters).some((group) => {
        if ('type' in group && group.type === 'dropdown-multiselect') {
            return group.selected?.length > 0;
        }
        return group.filters?.some((filter) => filter.isSelected !== (filter.defaultSelected ?? false));
    });
};
